import { type ComponentProps } from 'react';
import React from 'react';
import { LazySuspense, LazyWait, lazy } from 'react-loosely-lazy';
import { di } from 'react-magnetic-di';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { TriggerProps } from '@atlaskit/popup/types';
import { type SearchPromptPopup as SearchPromptPopupType } from './search-prompt-popup';

export const LazySearchPromptPopup = lazy(() =>
	import(/* webpackChunkName: "search-prompt-popup" */ './search-prompt-popup').then(
		(module) => module.SearchPromptPopup,
	),
);

/**
 * Feature flag: jsm_hc_search_remove_invalid_popup_aria
 *
 * Stops applying Popup trigger aria-expanded/aria-haspopup to the search field
 * wrapper. Those attributes are not allowed on a generic div.
 */
export const SEARCH_REMOVE_INVALID_POPUP_ARIA_GATE = 'jsm_hc_search_remove_invalid_popup_aria';

export const isRemoveInvalidSearchPopupAriaEnabled = (): boolean =>
	FeatureGates.checkGate(SEARCH_REMOVE_INVALID_POPUP_ARIA_GATE);

const getFallbackTriggerProps = (): TriggerProps =>
	isRemoveInvalidSearchPopupAriaEnabled()
		? ({ ref: null } as TriggerProps)
		: {
				ref: null,
				'aria-expanded': false,
				'aria-haspopup': false,
			};

export const SearchPromptPopup: React.FC<ComponentProps<typeof SearchPromptPopupType>> = (
	props: ComponentProps<typeof SearchPromptPopupType>,
) => {
	di(LazySuspense, LazyWait, LazySearchPromptPopup);

	const fallbackTriggerProps = getFallbackTriggerProps();

	return __SERVER__ ? (
		<>{props.trigger(fallbackTriggerProps)}</>
	) : (
		<LazySuspense fallback={<>{props.trigger(fallbackTriggerProps)}</>}>
			<LazyWait until={props.isOpen}>
				<LazySearchPromptPopup {...props} />
			</LazyWait>
		</LazySuspense>
	);
};
